import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { isOperator as isOperatorRole, isPartner as isPartnerRole, type User } from 'shared';
import { api } from '../utils/api';

export type { User, UserRole } from 'shared';

/** Operators (and admins) work in the back-office; members live in workspaces. */
export function isOperator(user: User | null): boolean {
  return isOperatorRole(user?.role);
}

/** Admins curate what operators only use: the template library, who may apply from outside. */
export function isAdmin(user: User | null): boolean {
  return user?.role === 'admin';
}

/** Partners create companies and invite their people, in an area of their own. */
export function isPartner(user: User | null): boolean {
  return isPartnerRole(user?.role);
}

/** Where a user belongs after signing in. */
export function homePathFor(user: User | null): string {
  if (isPartner(user)) return '/partner';
  return isOperator(user) ? '/app/workspaces' : '/w';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  register: (data: RegisterData) => Promise<void>;
  /** Signs in from a token already issued by the API, e.g. after accepting an invite. */
  setSession: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
}

interface StoredSession {
  token: string | null;
  user: User | null;
}

const EMPTY_SESSION: StoredSession = { token: null, user: null };

function clearStoredSession() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch {
    // A browser that refuses storage still gets a working in-memory session.
  }
}

/** A stored session that is not a real user object is no session at all. */
function isUser(value: unknown): value is User {
  const candidate = value as Partial<User> | null;
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    (candidate.role === 'admin' ||
      candidate.role === 'operator' ||
      candidate.role === 'member' ||
      candidate.role === 'partner')
  );
}

/**
 * Read synchronously so the very first render already knows who is signed in:
 * no loading flash, and no route guard racing an async restore.
 *
 * Anything unreadable or malformed is treated as signed out: rendering the app
 * around a half-valid user is worse than sending someone back to the login page.
 */
function readStoredSession(): StoredSession {
  try {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (!token || !user) return EMPTY_SESSION;

    const parsed: unknown = JSON.parse(user);
    if (!isUser(parsed)) {
      clearStoredSession();
      return EMPTY_SESSION;
    }

    return { token, user: parsed };
  } catch {
    return EMPTY_SESSION;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<StoredSession>(readStoredSession);

  const setSession = useCallback((token: string, user: User) => {
    setSessionState({ token, user });
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch {
      // Same as above: persistence is a convenience, not a requirement.
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<{ token: string; user: User }>('/api/auth/login', {
        email,
        password,
      });

      setSession(response.token, response.user);
      return response.user;
    },
    [setSession]
  );

  const register = useCallback(async (data: RegisterData) => {
    await api.post('/api/auth/register', data);
  }, []);

  const logout = useCallback(() => {
    setSessionState(EMPTY_SESSION);
    clearStoredSession();
  }, []);

  const updateUser = useCallback((user: User) => {
    setSessionState((current) => {
      if (current.token) {
        try {
          localStorage.setItem('user', JSON.stringify(user));
        } catch {
          // ignored, see setSession
        }
      }
      return { ...current, user };
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...session, login, register, setSession, logout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
